import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Pencil, Trash2, Check, X } from 'lucide-react'
import styles from './SavedFiltersSelect.module.css'
import SecureInput from '../../../ui/SecureInput/SecureInput'
import { useToolFilterPresetsStore } from '../../../store/toolFilterPresetsStore'
import { useToolsListViewStore } from '../../../store/toolsListViewStore'
import { matchesPresetFilters } from '../../../lib/toolsFilters'
import { ToolFilterPreset } from '../../../types/types'

const SavedFiltersSelect: React.FC = () => {
    const { presets, renamePreset, deletePreset } = useToolFilterPresetsStore()
    const store = useToolsListViewStore()

    const [open, setOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [editingName, setEditingName] = useState('')

    const wrapperRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!open) return
        const handleClick = (e: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
                setOpen(false)
                setEditingId(null)
            }
        }
        document.addEventListener('mousedown', handleClick)
        return () => document.removeEventListener('mousedown', handleClick)
    }, [open])

    const currentFilters = {
        statusFilter: store.statusFilter,
        methodFilter: store.methodFilter,
        environmentFilter: store.environmentFilter,
        connectionFilter: store.connectionFilter,
        selectedTags: store.selectedTags,
        sortBy: store.sortBy,
        searchTerm: store.searchTerm,
    }
    const activePreset = presets.find(p =>
        p.id === store.activePresetId && matchesPresetFilters(currentFilters, p.filters))

    const handleSelect = (preset: ToolFilterPreset) => {
        store.applyFilters(preset.filters, preset.id)
        setOpen(false)
    }
    const handleSelectAll = () => {
        store.setActivePresetId(null)
        setOpen(false)
    }
    const startRename = (preset: ToolFilterPreset) => {
        setEditingId(preset.id)
        setEditingName(preset.name)
    }

    const handleRenameConfirm = async (id: string) => {
        const name = editingName.trim()
        if (name) await renamePreset(id, name)
        setEditingId(null)
    }

    return (
        <div className={styles.wrapper} ref={wrapperRef}>
            <label className={styles.label}>Saved Filters:</label>
            <div className={styles.selectOuter}>
                <div
                    className={styles.select + (open ? ' ' + styles.open : '')}
                    tabIndex={0}
                    role="combobox"
                    aria-haspopup="listbox"
                    aria-expanded={open}
                    onClick={() => { setOpen(o => !o); setEditingId(null) }}
                    onKeyDown={e => {
                        if (e.key === 'Escape') setOpen(false)
                        if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
                            e.preventDefault()
                            setOpen(o => !o)
                        }
                    }}
                >
                    <span className={styles.valueText}>
                        {activePreset?.name ?? 'All'}
                    </span>
                    <span className={styles.chevron} aria-hidden="true"><ChevronDown /></span>
                </div>

                {open && (
                    <div role="listbox" className={styles.optionsPanel}>
                        <div
                            role="option"
                            aria-selected={!activePreset}
                            className={styles.option + (!activePreset ? ' ' + styles.optionSelected : '')}
                            onClick={handleSelectAll}
                        >
                            <span className={styles.optionName}>All</span>
                            {!activePreset && <span className={styles.checkMark}>✓</span>}
                        </div>
                        {presets.length === 0 ? (
                            <div className={styles.emptyMsg}>No saved filters yet. Use “Presets” to save the current filters.</div>
                        ) : presets.map(preset => {
                            const selected = activePreset?.id === preset.id
                            if (editingId === preset.id) {
                                return (
                                    <div key={preset.id} className={styles.renameRow}>
                                        <div className={styles.renameInput}>
                                            <SecureInput
                                                fieldType="name"
                                                fieldName="Filter name"
                                                value={editingName}
                                                onChange={setEditingName}
                                                autoFocus
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter') handleRenameConfirm(preset.id)
                                                    if (e.key === 'Escape') setEditingId(null)
                                                }}
                                            />
                                        </div>
                                        <button type="button" className={styles.iconBtn} onClick={() => handleRenameConfirm(preset.id)} title="Save name">
                                            <Check size={13} />
                                        </button>
                                        <button type="button" className={styles.iconBtn} onClick={() => setEditingId(null)} title="Cancel">
                                            <X size={13} />
                                        </button>
                                    </div>
                                )
                            }
                            return (
                                <div
                                    key={preset.id}
                                    role="option"
                                    aria-selected={selected}
                                    className={styles.option + (selected ? ' ' + styles.optionSelected : '')}
                                    onClick={() => handleSelect(preset)}
                                    title={preset.name}
                                >
                                    <span className={styles.optionName}>{preset.name}</span>
                                    {selected && <span className={styles.checkMark}>✓</span>}
                                    <button
                                        type="button"
                                        className={styles.iconBtn}
                                        onClick={e => { e.stopPropagation(); startRename(preset) }}
                                        title="Rename"
                                        aria-label={`Rename ${preset.name}`}
                                    >
                                        <Pencil size={12} />
                                    </button>
                                    <button
                                        type="button"
                                        className={`${styles.iconBtn} ${styles.iconBtnDanger}`}
                                        onClick={e => { e.stopPropagation(); deletePreset(preset.id) }}
                                        title="Delete"
                                        aria-label={`Delete ${preset.name}`}
                                    >
                                        <Trash2 size={12} />
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>
        </div>
    )
}

export default SavedFiltersSelect
